import {
  Box,
  Button,
  Chip,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Card,
} from "@mui/material";

import { useState } from "react";
import { FiberManualRecord } from "@mui/icons-material";

interface SelectedQuantities {
  [key: number]: number;
}

interface TicketVariant {
  id: number;
  details: string;
  name: string;
  status: string;
  startDate: string;
  closeDate: string;
  price: number;
  quantityAvailable: number;
}

interface EventTicketsProps {
  tickets: TicketVariant[];
}

export const EventTicketsView = ({ tickets }: EventTicketsProps) => {
  const [selectedQuantities, setSelectedQuantities] =
    useState<SelectedQuantities>({});

  const handleQuantityChange = (id: number, newQuantity: number) => {
    setSelectedQuantities((prevQuantities) => ({
      ...prevQuantities,
      [id]: newQuantity,
    }));
  };

  const total = tickets.reduce((acc, curr) => {
    const quantity = selectedQuantities[curr.id] || 0;
    return acc + curr.price * quantity;
  }, 0);

  return (
    <Card
      sx={{
        padding: 3,
        mb: 5,
        borderRadius: 2,
      }}
    >
      {tickets.length > 0 ? (
        <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
          <Table sx={{ minWidth: "100%" }} aria-label="ticket table">
            <TableHead>
              <TableRow>
                <TableCell colSpan={8}>Tipo de ticket</TableCell>
                <TableCell align="center">Precio por entrada</TableCell>
                <TableCell align="center">Cantidad</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell colSpan={8}>
                    <Typography variant="body1">{ticket.name}</Typography>
                    {ticket.details && (
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <FiberManualRecord
                          sx={{ color: "secondary.dark", fontSize: "small" }}
                        />
                        <Typography variant="body2" sx={{ pl: 1 }}>
                          {ticket.details}
                        </Typography>
                      </Box>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    ${ticket.price.toLocaleString()}
                  </TableCell>
                  <TableCell align="center">
                    {ticket.quantityAvailable === 0 ? (
                      <Chip label="Agotada" color="secondary" />
                    ) : (
                      <Select
                        value={selectedQuantities[ticket.id] || 0}
                        onChange={(e) =>
                          handleQuantityChange(
                            ticket.id,
                            Math.min(
                              parseInt(e.target.value as string, 10),
                              ticket.quantityAvailable
                            )
                          )
                        }
                        displayEmpty
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 200,
                            },
                            sx: {
                              backgroundColor: "darkBackground.light",
                              "& .MuiMenuItem-root": {
                                color: "text.primary",
                                "&:hover": {
                                  backgroundColor: "darkBackground.dark",
                                },
                                "&.Mui-selected": {
                                  backgroundColor: "darkBackground.dark",
                                  "&:hover": {
                                    backgroundColor: "darkBackground.light",
                                  },
                                },
                              },
                            },
                          },
                        }}
                      >
                        {[...Array(ticket.quantityAvailable + 1).keys()].map(
                          (number) => (
                            <MenuItem key={number} value={number}>
                              {number}
                            </MenuItem>
                          )
                        )}
                      </Select>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={8} align="right">
                  Total:
                </TableCell>
                <TableCell align="center">
                  {total > 0 ? `$${total.toLocaleString()}` : "---"}
                </TableCell>
                <TableCell align="center">
                  <Button
                    variant="contained"
                    color="primary"
                    disabled={total === 0}
                  >
                    Comprar
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ p: 2, textAlign: "center" }}>
          <Typography variant="body1">No hay entradas disponibles.</Typography>
        </Box>
      )}
    </Card>
  );
};
